import { motion, AnimatePresence } from "framer-motion";
import "./Listing.css";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../../contexts/authContext";
import { doc, collection, onSnapshot, updateDoc, arrayUnion, query } from "firebase/firestore";
import db from "../../../firebase";

// Desired Sorting Order
const CATEGORY_ORDER = [
  "Hazrat Rasool-e-Khuda (s.a.w.a.)",
  "Hazrat Imam Ali (a.s.)",
  "Hazrat Fatima Zehra (s.a.)",
  "Hazrat Imam Hasan (a.s.)",
  "Hazrat Imam Husain (a.s.)",
  "Hazrat Imam Zainul Abideen (a.s.)",
  "Hazrat Imam Muhammad Baqir (a.s.)",
  "Hazrat Imam Jafar Sadiq (a.s.)",
  "Hazrat Imam Moosa Kazim (a.s.)",
  "Hazrat Imam Ali Reza (a.s.)",
  "Hazrat Imam Muhammad Taqi (a.s.)",
  "Hazrat Imam Ali Naqi (a.s.)",
  "Hazrat Imam Hasan Askari (a.s.)",
  "Hazrat Imam e Zamana (a.j.f.s.)",
  "Ahle Bait (a.s.)",
  "Islamic Events",
  "Muharram-ul-Haram"
];

const Listing = ({ data: selected = 'greeting' }) => {
  const { isLoggedIn } = useContext(AuthContext);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentImages, setCurrentImages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Local state for the category data, populated from Firestore or Cache
  const [categoryState, setCategoryState] = useState(() => {
    // Initial mount cache check
    const cached = localStorage.getItem(`gallery_cache_${selected}`);
    return cached ? JSON.parse(cached) : null;
  });
  const [loading, setLoading] = useState(!categoryState); // If cache exists, not loading initially

  // Admin state
  const [adminSelectedName, setAdminSelectedName] = useState("");
  const [adminNewUrl, setAdminNewUrl] = useState("");

  // Sync state from Firebase Realtime (Firestore)
  useEffect(() => {
    if (!selected) return;

    // Check cache for the new selection to show immediate data if available
    const cached = localStorage.getItem(`gallery_cache_${selected}`);
    if (cached) {
      setCategoryState(JSON.parse(cached));
      setLoading(false);
    } else {
      setCategoryState(null); // Clear previous data
      setLoading(true);
    }

    // Listen to the subcollection "categories" inside "gallery/{selected}"
    const q = query(collection(db, "gallery", selected, "categories"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let liveData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by defined order
      liveData.sort((a, b) => {
        const indexA = CATEGORY_ORDER.indexOf(a.name);
        const indexB = CATEGORY_ORDER.indexOf(b.name);

        // Items in our list come before items not in our list
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;

        return 0;
      });

      setCategoryState(liveData);
      setLoading(false);
      // Update Cache
      localStorage.setItem(`gallery_cache_${selected}`, JSON.stringify(liveData));

    }, (error) => {
      console.error("Error fetching gallery data:", error);
      setLoading(false);
    });

    // Reset admin selection
    setAdminSelectedName("");

    return () => unsubscribe();
  }, [selected]);

  // Skeleton Loader Component
  const SkeletonLoader = () => (
    <div className="months-wrapper" style={{ opacity: 1 }}>
      {[1, 2, 3, 4].map((n) => (
        <div key={n} className="month-block" style={{ background: "#eee", height: 300, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ width: "60%", height: 24, background: "#ddd", borderRadius: 4, margin: "10px 0 0 10px" }} />
          <div style={{ flex: 1, margin: 10, background: "#ddd", borderRadius: 8 }} />
        </div>
      ))}
    </div>
  );

  if (!selected) return <div>Select a greeting card to see details here.</div>;
  if (loading) return <SkeletonLoader />;
  if (!categoryState || categoryState.length === 0) return <div style={{ padding: 20 }}>No images found.</div>;

  // Open gallery modal
  const openGallery = (images, index) => {
    setCurrentImages(images);
    setCurrentIndex(index);
    setSelectedImage(images[index]);
  };

  // Helper to extract all names for dropdown
  const allNames = categoryState.map(item => item.name);

  // Admin Update Handler via Firebase
  const handleAdminUpdate = async () => {
    if (!adminSelectedName || !adminNewUrl) {
      alert("Please select a name and enter a URL");
      return;
    }

    try {
      // Find the document ID for the selected name
      const targetDoc = categoryState.find(item => item.name === adminSelectedName);
      if (!targetDoc) {
        alert("Category not found!");
        return;
      }

      const docRef = doc(db, "gallery", selected, "categories", targetDoc.id);

      // Update Firestore
      await updateDoc(docRef, {
        images: arrayUnion(adminNewUrl)
      });

      setAdminNewUrl("");
      alert("Image added successfully!");
    } catch (error) {
      console.error("Update failed:", error);
      alert("Failed to add image: " + error.message);
    }
  };

  // New: stacked/deck view component
  const StackGallery = ({ images = [], onOpen }) => {
    const [topIdx, setTopIdx] = useState(images.length - 1);

    return (
      <div className="stack-wrapper" role="list" aria-label="stacked gallery">
        {images.map((url, i) => {
          const posFromTop = i - topIdx;
          // small offsets for behind/under effect
          const x = posFromTop * 8;
          const y = Math.abs(posFromTop) * 6;
          const rot = posFromTop * 3;
          const scale = i === topIdx ? 1 : 0.95;
          const z = i === topIdx ? images.length + 10 : i;
          return (
            <motion.img
              key={i}
              src={url}
              alt={`stack-${i}`}
              className="stack-card"
              style={{ zIndex: z }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ x, y, rotate: rot, scale, opacity: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              whileHover={{ scale: i === topIdx ? 1.02 : 0.97, y: y - 4 }}
              onClick={() => {
                setTopIdx(i);
                onOpen(images, i);
              }}
            />
          );
        })}
      </div>
    );
  };

  // Prev/Next 
  const prevImage = () => {
    setCurrentIndex((prev) => {
      const newIndex = (prev - 1 + currentImages.length) % currentImages.length;
      setSelectedImage(currentImages[newIndex]);
      return newIndex;
    });
  };

  const nextImage = () => {
    setCurrentIndex((prev) => {
      const newIndex = (prev + 1) % currentImages.length;
      setSelectedImage(currentImages[newIndex]);
      return newIndex;
    });
  };

  // Download
  const downloadImage = (url) => {
    fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `image-${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
  };

  const filteredCategory = (() => {
    const q = searchTerm.trim().toLowerCase();
    if (!categoryState) return [];
    if (!q) return categoryState;
    return categoryState.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        (item.images && item.images.some((img) => img.toLowerCase().includes(q)))
    );
  })();

  return (
    <div className="gallery-container">
      {/* Admin Section */}
      {isLoggedIn && (
        <div style={{ marginBottom: 20, padding: 15, border: "1px solid #ccc", borderRadius: 8, background: "#f9f9f9" }}>
          <h3>Admin: Add Image</h3>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <select
              value={adminSelectedName}
              onChange={(e) => setAdminSelectedName(e.target.value)}
              style={{ padding: 8, borderRadius: 4 }}
            >
              <option value="">Select Category Name</option>
              {allNames.map((name, idx) => (
                <option key={idx} value={name}>{name}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Paste Image URL"
              value={adminNewUrl}
              onChange={(e) => setAdminNewUrl(e.target.value)}
              style={{ padding: 8, borderRadius: 4, flex: 1 }}
            />
            <button
              onClick={handleAdminUpdate}
              style={{ padding: "8px 16px", background: "#007bff", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}
            >
              Update
            </button>
          </div>
        </div>
      )}

      <div
        className="gallery-header"
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}
      >
        <h2 className="gallery-main-title" style={{ margin: 0 }}>
          Our Image Gallery</h2>

        <input
          type="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name..."
          aria-label="Search by name"
          style={{
            padding: "8px 10px",
            borderRadius: 6,
            border: "1px solid #ccc",
            width: 220,
          }}
        />
      </div>

      <AnimatePresence>
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="months-wrapper"
        >
          {filteredCategory.map((item, idx) => (
            // Ensure images exists before checking length
            ((item.images && item.images.length > 0) || (isLoggedIn && item.name === adminSelectedName)) && (
              <div
                key={item.id || item.name}
                className={`month-block month-color-5`}
              >
                <h4 className="month-title">{item.name}</h4>
                {item.images && item.images.length > 0 ? (
                  <div className="month-images">
                    {/* stacked / deck style view */}
                    <StackGallery images={item.images} onOpen={openGallery} />
                  </div>
                ) : (
                  <p>No images available for this category.</p>
                )}
              </div>
            )
          ))}
        </motion.div>
      </AnimatePresence>

      {/* Modal Gallery */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="gallery-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.img
              key={selectedImage}
              src={selectedImage}
              alt="Large view"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="modal-img"
            />
            {/* Added counter to use currentIndex */}
            <div style={{ color: "#fff", marginBottom: 10 }}>
              {currentIndex + 1} / {currentImages.length}
            </div>

            <div className="modal-btns">
              {currentImages.length > 1 && (
                <>
                  <button onClick={prevImage} className="gallery-btn">
                    Prev
                  </button>
                  <button onClick={nextImage} className="gallery-btn">
                    Next
                  </button>
                </>
              )}
              <button
                onClick={() => downloadImage(selectedImage)}
                className="gallery-btn"
              >
                Download
              </button>
              <button
                onClick={() => setSelectedImage(null)}
                className="gallery-btn"
              >
                Close
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Listing;